import { PriceVariantDTO, ProductTileResponseSupplier, SupplierPreference } from '@api/generated/search-service';
import dayjs from 'dayjs';

/**
 * Jediné místo na FE, které rozhoduje, která cenová varianta je "ta nabízená" — tzn. ta, jejíž
 * cena je na dlaždici, ta, která se přidá do košíku, a ta, která je předvybraná v detailu.
 *
 * Pravidlo (shodné s BE `PriceVariantOrdering`):
 *   1. označení dodavatele zúží seznam — dokud existuje varianta se srdíčkem, vybírá se jen
 *      z nich; negativně označená vyhraje jen tehdy, když žádná jiná není,
 *   2. uvnitř té skupiny vyhraje **nejnižší aukční cena** — jediná cena, která jde mezi
 *      dodavateli porovnat (currentPrice je za balení, to se srovnávat nedá),
 *   3. při shodě má přednost garantovaná varianta.
 *
 * Kdyby se tohle pravidlo rozešlo s BE, dlaždice ukáže jinou cenu, než jakou uživatel dostane
 * v detailu a v košíku.
 */

/** Společný tvar pro dlaždici (`ProductTileResponseSupplier`) i detail (`PriceVariantDTO`). */
type OfferCandidate = {
  supplierId: number;
  auctionPrice: number;
  isGaranted: boolean;
};

/** Tie-break při shodné aukční ceně: garantovaná varianta má přednost před negarantovanou. */
export const guaranteedFirst = (a: { isGaranted: boolean }, b: { isGaranted: boolean }) => Number(b.isGaranted) - Number(a.isGaranted);

/**
 * Označení dodavatele u produktu zužuje seznam, ze kterého se pak vybírá podle ceny
 * (stejné pořadí jako `PriceVariantOrdering.rankOf` na BE).
 */
export const preferenceRank = (preference?: SupplierPreference | null): number => {
  if (preference === SupplierPreference.Preferred) return 0;
  if (preference === SupplierPreference.NonPreferred) return 2;
  return 1;
};

/**
 * Porovnání dvou kandidátů: označení -> aukční cena -> garantovaná varianta.
 * Dodavatel bez aukční ceny (0) jde na konec, aby nevyhrál "zadarmo".
 */
const compareOffers = (
  a: OfferCandidate,
  b: OfferCandidate,
  preferenceBySupplierId: Record<number, SupplierPreference | null | undefined>,
): number => {
  const rankDiff = preferenceRank(preferenceBySupplierId[a.supplierId]) - preferenceRank(preferenceBySupplierId[b.supplierId]);
  if (rankDiff !== 0) return rankDiff;

  const aPrice = a.auctionPrice > 0 ? a.auctionPrice : Infinity;
  const bPrice = b.auctionPrice > 0 ? b.auctionPrice : Infinity;

  return aPrice - bPrice || guaranteedFirst(a, b);
};

/**
 * Platí varianta pro tenhle nákup? Rozhoduje **den závozu dodavatele**, ne "teď" - nakupuje se
 * dopředu, takže cena, která začne platit až za pár dní, je legitimní volba. Stejné pravidlo drží
 * BE (`PriceVariantValidity`), jinak by šlo něco vidět a nešlo koupit.
 *
 * Bez data závozu (dodavatel v okně nezaváží) neplatí nic - BE takového dodavatele v detailu
 * vůbec nevrací, tohle je jen pojistka.
 */
export const isVariantValidOn = (variant: { validFrom: string; validTo: string }, deliveryDate?: string | null): boolean => {
  if (!deliveryDate) return false;
  const day = dayjs(deliveryDate).startOf('day');
  return !dayjs(variant.validFrom).startOf('day').isAfter(day) && !dayjs(variant.validTo).startOf('day').isBefore(day);
};

/** Mapa supplierId -> označení, jak ji nese dlaždice (označení je na dodavateli dlaždice). */
const preferencesOfTileSuppliers = (suppliers: ProductTileResponseSupplier[]): Record<number, SupplierPreference | null | undefined> =>
  Object.fromEntries(suppliers.map(s => [s.supplierId, s.supplierPreference]));

/**
 * Seřadí dodavatele dlaždice tak, jak je řadí BE — `suppliers[0]` je nabízená varianta.
 * Potřebné jen tam, kde se seznam mění lokálně (změna srdíčka v detailu nad dlaždicí);
 * čerstvá odpověď z BE už seřazená je.
 */
export const sortTileSuppliersBestFirst = (suppliers: ProductTileResponseSupplier[]): ProductTileResponseSupplier[] => {
  const preferences = preferencesOfTileSuppliers(suppliers);
  return [...suppliers].sort((a, b) => compareOffers(a, b, preferences));
};

/**
 * Nabízená varianta dlaždice. BE posílá v `suppliers` **jen dostupné** dodavatele (cena platná
 * v den, kdy dodavatel zaváží) už seřazené, takže se tu dostupnost neposuzuje znovu — jen se
 * respektuje filtr dodavatelů z URL, o kterém BE neví.
 *
 * `null` = produkt na daný den koupit nejde (nebo ho filtr dodavatelů vyřadil).
 */
export const pickOfferedTileSupplier = (
  suppliers: ProductTileResponseSupplier[],
  selectedSupplierIds: number[] = [],
): ProductTileResponseSupplier | null => {
  const candidates = selectedSupplierIds.length > 0 ? suppliers.filter(s => selectedSupplierIds.includes(s.supplierId)) : suppliers;

  if (candidates.length === 0) return null;

  const preferences = preferencesOfTileSuppliers(candidates);
  return [...candidates].sort((a, b) => compareOffers(a, b, preferences))[0];
};

/**
 * Řazení řádků dodavatelů v detailu produktu. Navíc oproti dlaždici řeší dostupnost:
 * detail zobrazuje i dodavatele, kteří na den závozu nemají platnou cenu (řádek je zakázaný),
 * a ti patří až pod dostupné. První řádek tak odpovídá předvybrané variantě.
 */
export const sortDetailSuppliersBestFirst = <
  T extends { priceVariant: PriceVariantDTO; supplierId: number; supplierPreference?: SupplierPreference | null; deliveryDate?: string | null },
>(
  rows: T[],
): T[] => {
  const preferences = Object.fromEntries(rows.map(r => [r.supplierId, r.supplierPreference]));

  return [...rows].sort((a, b) => {
    // Nahoře to, co platí k datu závozu dodavatele — ne to, co platí zrovna teď.
    const availabilityDiff =
      (isVariantValidOn(a.priceVariant, a.deliveryDate) ? 0 : 1) - (isVariantValidOn(b.priceVariant, b.deliveryDate) ? 0 : 1);
    if (availabilityDiff !== 0) return availabilityDiff;

    return compareOffers(a.priceVariant, b.priceVariant, preferences);
  });
};

/**
 * Předvybraná varianta v detailu produktu — stejné pravidlo jako u dlaždice, jen nad plochým
 * seznamem všech variant všech dodavatelů (detail nabízí i další varianty téhož dodavatele).
 *
 * `isExpired` znamená, že žádná varianta neplatí ke dni závozu; vrací se pak aspoň ta nejlepší
 * neplatná, ať má detail co zobrazit.
 */
export const findCheapestPriceVariant = (
  variants: PriceVariantDTO[],
  selectedSupplierIds: number[],
  /** supplierId -> označení dodavatele; chybějící záznam = neutrální. */
  preferenceBySupplierId: Record<number, SupplierPreference | undefined> = {},
  /** supplierId -> den, kdy dodavatel doveze (z detailu produktu); chybějící = nedoveze. */
  deliveryDateBySupplierId: Record<number, string | undefined> = {},
): { variant: PriceVariantDTO | null; isExpired: boolean } => {
  const suppliersVariants = selectedSupplierIds.length > 0 ? variants.filter(t => selectedSupplierIds.includes(t.supplierId)) : variants;

  const bestFirst = (a: PriceVariantDTO, b: PriceVariantDTO) => compareOffers(a, b, preferenceBySupplierId);

  // Platnost se posuzuje výhradně podle validFrom/validTo proti dni závozu. Období po garanci má
  // vlastní "extended" variantu, kterou vyrábí PriceVariantPreprocessJob, takže je pokryté tímhle
  // filtrem — fallback přes basicPriceValidTo byl zrušený návrh a nepatří sem zpátky.
  const validVariant = suppliersVariants
    .filter(t => isVariantValidOn(t, deliveryDateBySupplierId[t.supplierId]))
    .sort(bestFirst)
    ?.firstOrNull();

  if (validVariant) return { variant: validVariant, isExpired: false };

  const expiredVariant = [...suppliersVariants].sort(bestFirst)?.firstOrNull();

  return { variant: expiredVariant, isExpired: true };
};
